import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Coins, Check, AlertCircle, Loader2, Bitcoin, ExternalLink } from "lucide-react";

const STATUS_LABELS = {
  waiting: "Venter på betaling",
  confirming: "Bekrefter på blokkjeden",
  confirmed: "Bekreftet",
  sending: "Sender til vår wallet",
  partially_paid: "Delvis betalt",
  finished: "Fullført",
  failed: "Feilet",
  expired: "Utløpt",
  refunded: "Refundert",
};

export default function KjopKryptoStatus() {
  const { purchaseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const pollTimer = useRef(null);

  const [data, setData] = useState(null);
  const [polling, setPolling] = useState(true);
  const userCancelled = searchParams.get("status") === "cancel";

  const fetchStatus = async () => {
    try {
      const r = await api.get(`/payments/nowpayments/status/${purchaseId}`);
      setData(r.data);
      if (r.data.status === "completed") {
        setPolling(false);
        if (refresh) refresh();
        toast.success(`+${r.data.tokens} tokens kreditert!`);
      } else if (r.data.status === "failed") {
        setPolling(false);
      }
    } catch (e) {
      // 404 e.l. – stopp polling
      setPolling(false);
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line
  }, [purchaseId]);

  useEffect(() => {
    if (!polling) return;
    pollTimer.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(pollTimer.current);
    // eslint-disable-next-line
  }, [polling]);

  const status = data?.status;
  const npStatus = data?.nowpayments_payment_status;

  return (
    <Layout withSidebar={false}>
      <div className="max-w-xl mx-auto" data-testid="crypto-status-page">
        <div className="font-meta mb-3 flex items-center gap-2">
          <Bitcoin className="h-4 w-4 text-orange-400" />
          Krypto-betaling
        </div>
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-8 leading-tight">
          {status === "completed" ? "Betaling fullført." :
           status === "failed" ? "Betaling feilet." :
           userCancelled ? "Betaling avbrutt." :
           "Venter på din betaling…"}
        </h1>

        {/* Pending */}
        {!status || status === "pending" ? (
          <div className="border border-white/10 p-8 space-y-6" data-testid="crypto-pending-card">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 text-[var(--magenta)] animate-spin" />
              <div className="font-manrope text-[var(--cream)]">
                {userCancelled ? (
                  <>Du avbrøt betalingen på NOWPayments-siden. Du kan starte en ny betaling.</>
                ) : npStatus ? (
                  <>Status: <strong>{STATUS_LABELS[npStatus] || npStatus}</strong></>
                ) : (
                  <>Sjekker status hvert 5. sekund…</>
                )}
              </div>
            </div>

            <div className="border-l-4 border-[var(--magenta)] bg-[var(--magenta)]/5 p-4">
              <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed">
                Når du har sendt krypto til adressen på NOWPayments-siden, vil tokens
                automatisk krediteres her – vanligvis innen 1–3 minutter etter blokkjede-bekreftelse.
                Du kan trygt lukke denne siden, men hold den åpen for live-status.
              </p>
            </div>

            {data?.invoice_url && (
              <a href={data.invoice_url} target="_blank" rel="noopener noreferrer"
                 className="btn-ghost w-full inline-flex items-center justify-center gap-2"
                 data-testid="reopen-invoice-btn">
                <ExternalLink className="h-4 w-4" /> Åpne betalingssiden på nytt
              </a>
            )}

            <button onClick={() => navigate("/kjop")} className="btn-ghost w-full"
              data-testid="cancel-purchase-btn">
              Avbryt og gå tilbake
            </button>
          </div>
        ) : null}

        {/* Completed */}
        {status === "completed" && (
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-8" data-testid="crypto-success">
            <Check className="h-10 w-10 text-emerald-400 mb-4" />
            <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Tokens kreditert!</h2>
            <div className="space-y-2 mb-6">
              <div className="font-manrope text-base text-[var(--cream)] inline-flex items-center gap-2">
                <Coins className="h-4 w-4 text-[var(--gold)]" />
                <span data-testid="crypto-tokens-credited">+{data.tokens} tokens</span> kreditert
              </div>
              <div className="font-manrope text-sm text-[var(--cream-soft)]">
                Ny saldo: <strong className="text-[var(--cream)]">{data.new_balance} tokens</strong>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/wallet" className="btn-magenta inline-flex items-center gap-2" data-testid="go-wallet-crypto">
                <Coins className="h-4 w-4" /> Til min wallet
              </Link>
              <button onClick={() => navigate("/kjop")} className="btn-ghost">Kjøp flere</button>
            </div>
          </div>
        )}

        {/* Failed */}
        {status === "failed" && (
          <div className="border border-red-500/30 bg-red-500/5 p-8" data-testid="crypto-failed">
            <AlertCircle className="h-10 w-10 text-red-400 mb-4" />
            <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">
              Betaling ikke gjennomført
            </h2>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-6">
              {npStatus === "expired"
                ? "Fakturaen utløp før betaling ble registrert. Ingen krypto er trukket fra din wallet."
                : "Betalingen ble avbrutt eller feilet. Ingen tokens er kreditert."}
            </p>
            <button onClick={() => navigate("/kjop")} className="btn-magenta">Prøv igjen</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
